// Platform admins page (Phase 5c). Invite + disable + MFA reset + support-login toggle.

import { useEffect, useState } from 'react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import type { GridCustomCellProps } from '@progress/kendo-react-grid';
import { Button } from '@progress/kendo-react-buttons';
import { Switch } from '@progress/kendo-react-inputs';
import { Notification, NotificationGroup } from '@progress/kendo-react-notification';
import type {
  PlatformAdminInviteRequest,
  PlatformAdminUpdateRequest,
  PlatformAdminUserDto,
} from '@shared/api-types';
import { adminApiFetch } from '../../lib/adminApiClient';
import { useElementWidth, toResponsiveWidth } from '@shared/useElementWidth';

export function PlatformAdmins() {
  const [rows, setRows] = useState<PlatformAdminUserDto[] | null>(null);
  // Container-measured column shedding, matching the customer app's grids: the
  // grid's own width, not the viewport's, because the drawer rail changes what
  // is available by ~200px. See DESIGN.md ("Responsive").
  const [gridRef, gridWidth] = useElementWidth();
  const w = toResponsiveWidth(gridWidth);
  const [toast, setToast] = useState<string | null>(null);
  // Id of the admin whose support-login flag is mid-flight, so the switch
  // can't be double-toggled while the PUT is in the air.
  const [savingSupportFor, setSavingSupportFor] = useState<string | null>(null);

  const reload = async () => {
    const data = await adminApiFetch<PlatformAdminUserDto[]>('/api/admin/platform-admins');
    setRows(data);
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      const data = await adminApiFetch<PlatformAdminUserDto[]>('/api/admin/platform-admins');
      if (active) setRows(data);
    })();
    return () => {
      active = false;
    };
  }, []);

  const invite = async () => {
    const email = window.prompt('Admin email');
    if (!email) return;
    const display = window.prompt('Display name', email) ?? email;
    try {
      const body: PlatformAdminInviteRequest = { email, displayName: display };
      await adminApiFetch<PlatformAdminUserDto>('/api/admin/platform-admins', { method: 'POST', body });
      await reload();
      setToast(`Invited ${email}. They will receive a sign-in code by email.`);
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Invite failed.');
    }
  };

  const disable = async (id: string) => {
    if (!window.confirm('Disable this admin?')) return;
    try {
      await adminApiFetch<void>(`/api/admin/platform-admins/${id}/disable`, { method: 'POST' });
      await reload();
      setToast('Disabled.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Disable failed.');
    }
  };

  // Toggle the CanImpersonate allow-list flag that gates the "Log in as
  // support" panel on the tenant page. Granting is confirmed (it opens every
  // active tenant to that admin); revoking is not.
  const setSupportLogin = async (row: PlatformAdminUserDto, next: boolean) => {
    // Kendo's Switch renders a span[aria-disabled], not a natively disabled
    // control, so `disabled` still fires onChange on click. The API refuses
    // this too (409) — this just avoids a pointless request and error toast.
    if (row.isDisabled) return;
    if (next && !window.confirm(
      `Allow ${row.email} to log in as support into any active tenant?`,
    )) {
      return;
    }
    setSavingSupportFor(row.id);
    try {
      // Send ONLY the flag: including the display name from this (possibly
      // stale) row would revert a rename made elsewhere since the last load.
      const body: PlatformAdminUpdateRequest = { canImpersonate: next };
      await adminApiFetch<PlatformAdminUserDto>(`/api/admin/platform-admins/${row.id}`, {
        method: 'PUT',
        body,
      });
      await reload();
      setToast(
        next
          ? `Support login enabled for ${row.email}.`
          : `Support login disabled for ${row.email}.`,
      );
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Could not update support login.');
    } finally {
      setSavingSupportFor(null);
    }
  };

  const mfaReset = async (id: string) => {
    if (!window.confirm('Reset MFA for this admin? They will enroll again on next sign-in.')) return;
    try {
      await adminApiFetch<void>(`/api/admin/platform-admins/${id}/mfa-reset`, { method: 'POST' });
      await reload();
      setToast('MFA reset.');
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Reset failed.');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Platform admins</h2>
        <Button themeColor="primary" onClick={() => void invite()}>Invite admin</Button>
      </div>
      <div ref={gridRef}>
      <Grid data={rows ?? []} style={{ height: 'calc(100dvh - 200px)' }}>
        {/* Email is identity and never drops. Display name duplicates it closely
            enough to shed, and last-login is history rather than state. The two
            state flags (MFA, Disabled) are cheap, so they survive longest. */}
        <GridColumn field="email" title="Email" />
        {w >= 820 && <GridColumn field="displayName" title="Display name" />}
        <GridColumn field="mfaEnrolled" title="MFA" width="80px" />
        <GridColumn field="isDisabled" title="Disabled" width="100px" />
        {w >= 640 && <GridColumn field="lastLoginUtc" title="Last login" width="180px" />}
        <GridColumn
          title="Support login"
          width="140px"
          cells={{ data: (props: GridCustomCellProps) => {
            const row = props.dataItem as PlatformAdminUserDto;
            return (
              // Kendo's Switch takes no `title`, so the tooltip lives on the cell.
              <td
                title={
                  row.isDisabled
                    ? 'Disabled admins cannot be granted support login'
                    : 'Allow this admin to log in as support into active tenants'
                }
              >
                <Switch
                  checked={row.canImpersonate}
                  disabled={savingSupportFor === row.id || row.isDisabled}
                  onLabel="On"
                  offLabel="Off"
                  size="small"
                  ariaLabel={`Support login for ${row.email}`}
                  onChange={(e) => void setSupportLogin(row, e.value)}
                />
              </td>
            );
          } }}
        />
        <GridColumn
          title="Actions"
          width="220px"
          cells={{ data: (props: GridCustomCellProps) => {
            const row = props.dataItem as PlatformAdminUserDto;
            return (
              <td style={{ display: 'flex', gap: 6 }}>
                <Button size="small" onClick={() => void mfaReset(row.id)}>MFA reset</Button>
                {!row.isDisabled && (
                  <Button size="small" onClick={() => void disable(row.id)}>Disable</Button>
                )}
              </td>
            );
          } }}
        />
      </Grid>
      </div>

      <NotificationGroup style={{ position: 'fixed', right: 16, bottom: 16 }}>
        {toast && (
          <Notification type={{ style: 'info', icon: true }} closable onClose={() => setToast(null)}>
            <span>{toast}</span>
          </Notification>
        )}
      </NotificationGroup>
    </div>
  );
}
